(() => {
  const section = document.querySelector('.pdp');
  if (!section) return;

  const form = section.querySelector('[data-product-form]');
  const qtyInput = section.querySelector('[data-qty-input]');
  const addButton = section.querySelector('[data-add-button]');
  const stickyBar = section.querySelector('[data-sticky-bar]');
  const stickyAdd = section.querySelector('[data-sticky-add]');

  // ---------- gallery ----------

  // The swipe itself is the browser's, on a scroll snap track. Everything here
  // only mirrors where that scroller already is.
  const slides = section.querySelector('[data-slides]');

  if (slides) {
    const items = [...slides.querySelectorAll('[data-slide]')];
    const thumbs = [...section.querySelectorAll('[data-thumb]')];
    const dots = [...section.querySelectorAll('.pdp__dot')];
    let index = 0;

    // ---------- video ----------

    // A video left playing behind the photograph a shopper has moved to is
    // sound coming from nowhere and a download still running for something off
    // screen.
    //
    // Driven by the scroller's own position, not by mark(). The observer that
    // paints the dots counts two slides as showing at once during a swipe and
    // marks whichever fires last, so its index arrives a step behind: asked to
    // pause "everything except the current slide" it would pause the
    // photographs and leave the video running.
    //
    // Autoplay is a separate matter and is off: see the setting on the section.
    const pauseHidden = (current) => {
      items.forEach((slide, i) => {
        if (i === current) return;
        const video = slide.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    };

    // Read from the scroller rather than from the observer's index. The
    // observer is what paints the dots, but it fires a frame late, so an arrow
    // pressed twice quickly was working from a stale position and stalled.
    const currentIndex = () => Math.round(slides.scrollLeft / (slides.clientWidth || 1));

    // The gallery loops. Stopping dead at the last image is a dead end the
    // shopper has to reverse out of, and "next" that does nothing reads as
    // broken. A wrap travels the whole track, so it jumps rather than sliding
    // the long way round.
    // A wrap crosses the whole track, so it jumps rather than sliding past
    // every image in between. The jump alone read as a hard cut, so the
    // arriving image plays the same entrance the swipe gives it: it comes in
    // from the side it would have come from.
    const arrive = (position, from) => {
      const img = items[position]?.querySelector('img');
      if (!img || reduced.matches) return;

      img.animate(
        [
          {
            transform: `translate3d(${from * 24}%, 0, -220px) rotateY(${from * -26}deg) scale(0.86)`,
            opacity: 0,
            filter: 'blur(6px) saturate(0.45)',
          },
          { transform: 'none', opacity: 1, filter: 'none' },
        ],
        { duration: 460, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    };

    const goTo = (next) => {
      const count = items.length;
      if (!count) return;

      const wrapped = ((next % count) + count) % count;
      const isWrap = next < 0 || next >= count;

      items[wrapped]?.scrollIntoView({
        behavior: isWrap ? 'instant' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });

      if (isWrap) arrive(wrapped, next >= count ? 1 : -1);
    };

    const mark = (next) => {
      index = next;
      thumbs.forEach((thumb, i) => thumb.classList.toggle('pdp__thumb--active', i === next));
      dots.forEach((dot, i) => dot.classList.toggle('pdp__dot--on', i === next));
      // Scrolled directly rather than with scrollIntoView, which would also
      // move the page vertically to reach the strip.
      const thumb = thumbs[next];
      const strip = thumb?.parentElement;
      if (thumb && strip) {
        const left = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
        strip.scrollTo({ left, behavior: 'smooth' });
      }
    };

    // Whichever slide covers most of the scrollport is the current one, which
    // is also true mid swipe, so the dots track the thumb rather than jumping
    // at the end.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) mark(items.indexOf(entry.target));
          });
        },
        { root: slides, threshold: 0.6 }
      );
      items.forEach((item) => observer.observe(item));
    }

    // ---------- swap transition ----------

    // Driven from the scroll position rather than animation-timeline, which
    // iOS Safari does not support, so the effect is identical on every device
    // and follows the thumb through a momentum scroll.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let queued = false;

    const paint = () => {
      queued = false;
      const width = slides.clientWidth || 1;
      const offset = slides.scrollLeft;

      items.forEach((item, i) => {
        const img = item.querySelector('img');
        if (!img) return;

        // -1 fully to the left, 0 centred, 1 fully to the right.
        const p = Math.max(-1.3, Math.min(1.3, (i * width - offset) / width));
        const away = Math.min(1, Math.abs(p));
        // Eased, so the image holds its ground near centre and falls away
        // quickly once it commits to leaving.
        const gone = away * away;

        // The image drifts at a fraction of the track's speed, so it looks
        // like it sits behind the frame rather than being carried by it.
        img.style.transform =
          `translate3d(${(p * 24).toFixed(2)}%, 0, ${(-gone * 220).toFixed(1)}px)` +
          ` rotateY(${(p * -26).toFixed(2)}deg) scale(${(1 - gone * 0.14).toFixed(3)})`;
        img.style.opacity = String(Math.max(0, 1 - gone * 1.15));
        img.style.filter =
          away > 0.02
            ? `blur(${(gone * 6).toFixed(2)}px) saturate(${(1 - gone * 0.55).toFixed(2)}) brightness(${(1 + gone * 0.12).toFixed(2)})`
            : '';

        // The trailing edge closes in as the image leaves, so it is wiped away
        // rather than simply slid off.
        const cut = (gone * 26).toFixed(2);
        img.style.clipPath =
          away > 0.02
            ? `inset(${(gone * 6).toFixed(2)}% ${p < 0 ? cut : 0}% ${(gone * 6).toFixed(2)}% ${p > 0 ? cut : 0}% round 20px)`
            : '';
      });
    };

    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    };

    if (items.length > 1 && !reduced.matches) {
      slides.classList.add('pdp__slides--live');
      slides.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      paint();
    }

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

    // rAF throttled: a scroll fires far more often than a slide changes.
    let pauseTick = null;
    const pauseOnMove = () => {
      if (pauseTick) return;
      pauseTick = requestAnimationFrame(() => {
        pauseTick = null;
        pauseHidden(currentIndex());
      });
    };

    slides.addEventListener('scroll', pauseOnMove, { passive: true });

    // The arrows beside the thumbnails change the image rather than paging the
    // strip. Scrolling a strip the shopper can already swipe is a control that
    // does nothing they wanted; moving the picture is the job. mark() keeps the
    // active thumbnail in view on its own.
    const stripPrev = section.querySelector('[data-thumb-prev]');
    const stripNext = section.querySelector('[data-thumb-next]');

    if (stripPrev && stripNext) {
      stripPrev.hidden = items.length < 2;
      stripNext.hidden = items.length < 2;
      stripPrev.addEventListener('click', () => goTo(currentIndex() - 1));
      stripNext.addEventListener('click', () => goTo(currentIndex() + 1));
    }

    slides.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') goTo(currentIndex() - 1);
      if (event.key === 'ArrowRight') goTo(currentIndex() + 1);
    });

    // A scroll snap track stops at its own edges, so a swipe past the last
    // image did nothing at all. Catching the gesture at the boundary and
    // wrapping keeps the loop consistent with the arrows.
    if (items.length > 1) {
      let swipeFrom = 0;

      slides.addEventListener(
        'touchstart',
        (event) => {
          swipeFrom = event.touches[0].clientX;
        },
        { passive: true }
      );

      slides.addEventListener(
        'touchend',
        (event) => {
          const travelled = event.changedTouches[0].clientX - swipeFrom;
          if (Math.abs(travelled) < 50) return;

          // By index, not by scrollWidth. The swap animation translates each
          // image up to 24% of its slide, and transformed content counts
          // towards scrollable overflow, so the track measured 28px wider than
          // its slides and the last one never satisfied "at the end".
          const at = currentIndex();

          if (travelled < 0 && at >= items.length - 1) goTo(items.length);
          if (travelled > 0 && at <= 0) goTo(-1);
        },
        { passive: true }
      );
    }

    // ---------- zoom ----------

    // Every gesture is handled here rather than left to the browser. Handing
    // pinch to the browser zooms the page itself, which on a phone reads as
    // being thrown out of the site, and the scroll based version could not be
    // panned at all under touch-action: pinch-zoom.

    const zoom = section.querySelector('[data-zoom]');
    const zoomImg = zoom?.querySelector('[data-zoom-img]');
    const canvas = zoom?.querySelector('[data-zoom-canvas]');
    const note = zoom?.querySelector('[data-zoom-note]');

    const FIT = 1;
    const STEP = 2.4;
    const MAX = 5;

    const view = { scale: FIT, x: 0, y: 0 };
    let lastFocus = null;

    const glide = (on) => {
      zoomImg.style.transition = on ? 'transform 0.34s cubic-bezier(0.16, 1, 0.3, 1)' : '';
    };

    // Derived from the natural size rather than measured from the box, because
    // the box reflects whatever transform is currently applied and is zero
    // until the image has loaded — which silently pinned every zoom to the
    // centre of the frame.
    const fitted = () => {
      if (!zoomImg.naturalWidth) return null;
      const style = getComputedStyle(canvas);
      const room = {
        width: canvas.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
        height: canvas.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom),
      };
      const k = Math.min(room.width / zoomImg.naturalWidth, room.height / zoomImg.naturalHeight, 1);
      return { width: zoomImg.naturalWidth * k, height: zoomImg.naturalHeight * k };
    };

    // Keeps the image from being dragged off into empty space.
    const clamp = () => {
      const base = fitted();
      if (!base) return;
      const spareX = Math.max(0, (base.width * view.scale - canvas.clientWidth) / 2);
      const spareY = Math.max(0, (base.height * view.scale - canvas.clientHeight) / 2);
      view.x = Math.min(spareX, Math.max(-spareX, view.x));
      view.y = Math.min(spareY, Math.max(-spareY, view.y));
    };

    const draw = () => {
      clamp();
      zoomImg.style.transform = `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`;
      const magnified = view.scale > FIT + 0.01;
      canvas.classList.toggle('pdp__zoom-canvas--in', magnified);
      if (note) {
        note.textContent = magnified
          ? 'Drag to move · pinch to zoom · tap to fit'
          : 'Tap where you want a closer look';
      }
    };

    // Scales about a point so the pixel under the finger stays under it.
    const scaleAbout = (next, px, py) => {
      const target = Math.min(MAX, Math.max(FIT, next));
      const ratio = target / view.scale;
      view.x = px - ratio * (px - view.x);
      view.y = py - ratio * (py - view.y);
      view.scale = target;
      draw();
    };

    const reset = () => {
      view.scale = FIT;
      view.x = 0;
      view.y = 0;
      draw();
    };

    const closeZoom = () => {
      zoom.hidden = true;
      glide(false);
      reset();
      document.body.style.overflow = '';
      lastFocus?.focus();
    };

    // Waiting on the full size image left the viewer empty for as long as it
    // took to arrive. The already decoded gallery image opens instantly and is
    // swapped for the sharp one once that has loaded, so the shopper is never
    // looking at nothing.
    const upgrade = (fullSrc) => {
      if (!fullSrc || zoomImg.src === fullSrc) return;

      const full = new Image();
      full.decoding = 'async';
      full.addEventListener('load', () => {
        zoom.classList.remove('pdp__zoom--loading');
        // They may have closed it, or moved on to another image, in the
        // meantime.
        if (!zoom.hidden && zoomImg.dataset.want === fullSrc) zoomImg.src = fullSrc;
      });
      full.addEventListener('error', () => zoom.classList.remove('pdp__zoom--loading'));
      full.src = fullSrc;
    };

    const openZoom = (slide) => {
      lastFocus = slide;

      const shown = slide.querySelector('img');
      const fullSrc = slide.dataset.zoomSrc;

      zoomImg.dataset.want = fullSrc;
      zoomImg.src = shown?.currentSrc || shown?.src || fullSrc;
      zoom.classList.add('pdp__zoom--loading');

      zoom.hidden = false;
      document.body.style.overflow = 'hidden';
      glide(false);
      reset();
      zoom.querySelector('[data-zoom-close]').focus();

      upgrade(fullSrc);
    };

    // A full screen takeover is right on a phone, where the tile is small and
    // there is no hover. On a desktop it is a lot of ceremony for a closer
    // look, so there the image magnifies in place and the pointer pans it.
    const pointerIsFine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const stage = section.querySelector('.pdp__stage');
    let lensOn = false;

    const trackLens = (event) => {
      const box = stage.getBoundingClientRect();
      const x = ((event.clientX - box.left) / box.width) * 100;
      const y = ((event.clientY - box.top) / box.height) * 100;
      items[index]?.style.setProperty(
        'transform-origin',
        `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`
      );
    };

    const setLens = (on, event) => {
      lensOn = on;
      stage.classList.toggle('pdp__stage--lens', on);

      items.forEach((slide) => {
        slide.style.transform = '';
        slide.style.transformOrigin = '';
      });

      if (!on) return;
      if (event) trackLens(event);
      if (items[index]) items[index].style.transform = 'scale(2.2)';
    };

    stage.addEventListener('mousemove', (event) => {
      if (lensOn) trackLens(event);
    });

    stage.addEventListener('mouseleave', () => {
      if (lensOn) setLens(false);
    });

    if (zoom) {
      // A video slide has no still to magnify, and its own controls sit where
      // the click would land, so zoom and the hover lens both leave it alone.
      const isVideo = (slide) => slide.hasAttribute('data-slide-video');

      const openOrLens = (slide, event) => {
        if (!slide || isVideo(slide)) return;
        if (pointerIsFine.matches) setLens(!lensOn, event);
        else openZoom(slide);
      };

      items.forEach((slide) =>
        slide.addEventListener('click', (event) => openOrLens(slide, event))
      );
      section
        .querySelector('[data-zoom-open]')
        ?.addEventListener('click', (event) => openOrLens(items[index], event));

      // Warm the full size image the moment a finger lands or a cursor
      // arrives, so by the time the viewer opens it is usually already there.
      const warm = (slide) => {
        if (slide.dataset.warmed || !slide.dataset.zoomSrc) return;
        slide.dataset.warmed = 'true';
        new Image().src = slide.dataset.zoomSrc;
      };

      items.forEach((slide) => {
        slide.addEventListener('pointerdown', () => warm(slide), { passive: true });
        slide.addEventListener('pointerenter', () => warm(slide), { passive: true });
      });

      // The image on screen is the one most likely to be zoomed.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => warm(items[0]), { timeout: 3000 });
      } else {
        window.addEventListener('load', () => warm(items[0]), { once: true });
      }

      const points = new Map();
      let pinch = null;
      let moved = false;

      // Pointer coordinates relative to the canvas centre, which is where the
      // image's transform origin sits.
      const local = (clientX, clientY) => {
        const box = canvas.getBoundingClientRect();
        return { x: clientX - box.left - box.width / 2, y: clientY - box.top - box.height / 2 };
      };

      canvas.addEventListener('pointerdown', (event) => {
        // Throws NotFoundError if the pointer is already gone, and an
        // exception here would abandon the rest of the gesture setup.
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch (error) {
          /* capture is an optimisation, not a requirement */
        }
        points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        glide(false);
        if (points.size === 1) moved = false;

        if (points.size === 2) {
          const [a, b] = [...points.values()];
          pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: view.scale };
          moved = true;
        }
      });

      canvas.addEventListener('pointermove', (event) => {
        const previous = points.get(event.pointerId);
        if (!previous) return;
        points.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (points.size >= 2 && pinch) {
          const [a, b] = [...points.values()];
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          const middle = local((a.x + b.x) / 2, (a.y + b.y) / 2);
          scaleAbout((pinch.scale * distance) / pinch.distance, middle.x, middle.y);
          return;
        }

        if (view.scale <= FIT) return;
        const dx = event.clientX - previous.x;
        const dy = event.clientY - previous.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        view.x += dx;
        view.y += dy;
        draw();
      });

      const release = (event) => {
        points.delete(event.pointerId);
        if (points.size < 2) pinch = null;
        if (points.size > 0) return;

        // A tap, not a drag or a pinch.
        if (moved) return;

        if (event.target !== zoomImg) return closeZoom();

        glide(true);
        if (view.scale > FIT + 0.01) {
          reset();
        } else {
          const point = local(event.clientX, event.clientY);
          scaleAbout(STEP, point.x, point.y);
        }
      };

      canvas.addEventListener('pointerup', release);
      canvas.addEventListener('pointercancel', (event) => {
        points.delete(event.pointerId);
        if (points.size < 2) pinch = null;
      });

      canvas.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          const point = local(event.clientX, event.clientY);
          glide(false);
          scaleAbout(view.scale * (event.deltaY > 0 ? 0.88 : 1.14), point.x, point.y);
        },
        { passive: false }
      );

      window.addEventListener('resize', () => {
        if (!zoom.hidden) reset();
      });

      zoom.querySelector('[data-zoom-close]').addEventListener('click', closeZoom);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !zoom.hidden) closeZoom();
      });
    }
  }

  // ---------- imported description cleanup ----------

  // Supplier descriptions are full of paragraphs holding a single non-breaking
  // space. Liquid cannot match them, since the source is the U+00A0 character
  // rather than the entity, and CSS cannot select on emptiness. Each one costs
  // 55px of blank panel.
  section.querySelectorAll('[data-strip-blanks] > *').forEach((block) => {
    const blank = block.textContent.replace(/[\s ]/g, '') === '';
    if (blank && !block.querySelector('img, iframe, video')) block.remove();
  });

  // ---------- notices ----------

  const notice = section.querySelector('[data-add-notice]');

  const say = (message, tone = 'warn') => {
    if (!notice) return;
    notice.textContent = message || '';
    notice.dataset.tone = tone;
    notice.hidden = !message;
  };

  // ---------- quantity ----------

  const stockData = section.querySelector('[data-stock-data]');
  const stock = stockData ? JSON.parse(stockData.textContent) : {};
  const variantInputEl = section.querySelector('[data-variant-input]');

  const stockLimit = () => {
    const entry = stock[variantInputEl?.value];
    return entry && typeof entry.limit === 'number' ? entry.limit : null;
  };

  // Clamping here is what turns "nothing happened" into an answer. Shopify
  // would refuse the add anyway; this says so before the shopper commits.
  const clampQty = (announce) => {
    const limit = stockLimit();
    let wanted = Math.max(1, Math.floor(Number(qtyInput.value) || 1));

    if (limit !== null && wanted > limit) {
      wanted = Math.max(1, limit);
      if (announce) {
        say(limit > 0 ? `Only ${limit} left in stock.` : 'This one is out of stock.');
      }
    } else if (announce) {
      say('');
    }

    qtyInput.value = wanted;
    return wanted;
  };

  section.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
    qtyInput.value = Math.max(1, Number(qtyInput.value) - 1);
    say('');
  });

  section.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
    qtyInput.value = Number(qtyInput.value) + 1;
    clampQty(true);
  });

  qtyInput?.addEventListener('change', () => clampQty(true));

  // ---------- gift wrapping ----------

  const giftWrapBox = section.querySelector('[data-gift-wrap]');
  const giftNote = section.querySelector('[data-gift-note]');
  const giftMessage = section.querySelector('[data-gift-message]');
  const giftCount = section.querySelector('[data-gift-count]');

  // No autofocus on touch: it throws the keyboard up and scrolls the page out
  // from under the shopper before they have decided what to write.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  giftWrapBox?.addEventListener('change', () => {
    if (giftNote) giftNote.hidden = !giftWrapBox.checked;
    if (giftWrapBox.checked && finePointer.matches) giftMessage?.focus();
  });

  giftMessage?.addEventListener('input', () => {
    if (!giftCount) return;
    const max = Number(giftMessage.dataset.giftMax) || 100;
    // Counting down reads as a budget rather than a score.
    giftCount.textContent = String(Math.max(0, max - giftMessage.value.length));
  });

  // ---------- variants ----------

  const variantData = section.querySelector('[data-variant-data]');
  if (variantData) {
    const variants = JSON.parse(variantData.textContent);
    const selects = [...section.querySelectorAll('[data-option-index]')];
    const variantInput = section.querySelector('[data-variant-input]');
    const priceEl = section.querySelector('[data-price]');
    const stockEl = section.querySelector('[data-stock]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const instalmentEl = section.querySelector('[data-instalment]');

    const money = (cents) =>
      new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);

    const update = () => {
      const chosen = selects.map((select) => select.value);
      const match = variants.find((v) => chosen.every((value, index) => v.options[index] === value));
      if (!match) return;

      variantInput.value = match.id;
      priceEl.textContent = money(match.price);
      if (stickyPrice) stickyPrice.textContent = money(match.price);
      if (instalmentEl) instalmentEl.textContent = money(Math.floor(match.price / 4));

      if (stockEl) stockEl.hidden = match.available;
      clampQty(false);
      say('');

      [addButton, stickyAdd].forEach((button) => {
        if (!button) return;
        button.disabled = !match.available;
      });
      if (addButton) addButton.textContent = match.available ? 'Add to cart' : 'Out of stock';

      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    };

    selects.forEach((select) => select.addEventListener('change', update));
  }

  // ---------- add to cart, into the drawer ----------

  form?.addEventListener('submit', async (event) => {
    if (!window.StLukesCart) return; // fall back to a normal form post
    event.preventDefault();

    const quantity = clampQty(true);
    const giftWrap = giftWrapBox;

    const extras = [];
    if (giftWrap && giftWrap.checked) {
      const message = giftMessage?.value.trim();
      extras.push({
        id: Number(giftWrap.value),
        quantity: 1,
        // A line item property, so it travels to the cart, the order in admin
        // and the packing slip rather than living only in this page's state.
        properties: message ? { 'Gift message': message } : {},
      });
    }

    addButton.disabled = true;
    try {
      // Gift wrapping is one per order, not one per product. Lines carrying
      // different messages are different lines, so the old line is dropped and
      // the newest message wins rather than a second roll stacking up.
      if (extras.length) {
        try {
          await window.StLukesCart.clearVariant(giftWrap.value);
        } catch (capError) {
          console.warn('could not clear the previous gift wrapping line', capError);
        }
      }

      await window.StLukesCart.add(variantInputEl.value, quantity, extras);

      if (extras.length) {
        giftWrap.checked = false;
        if (giftNote) giftNote.hidden = true;
      }

      say('');
    } catch (error) {
      // Shopify's own wording is more accurate than anything invented here:
      // it knows how many are left and what is already in the cart.
      say(error.detail || 'That could not be added just now. Please try again.');
    } finally {
      addButton.disabled = false;
    }
  });

  stickyAdd?.addEventListener('click', () => form?.requestSubmit());

  // ---------- sticky bar ----------

  if (stickyBar && addButton && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      },
      { rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(addButton);
  }
})();
